import { Badge, Select, Space, Tag, Typography } from 'antd';
import { space } from '@viq-admin/ui';
import { CRM_COPY } from './copy';
import { HelpHint } from './HelpHint';
import type { CrmOwner, OwnerProvenance } from './types';

export interface OwnerPickerProps {
  owners: CrmOwner[];
  value: string | undefined; // explicit pick
  suggestedOwnerId: string | null; // rendered with the "Suggested" badge when value is undefined
  currentOwner: { id: string; name: string } | null;
  ownerRequired: boolean;
  /**
   * Where `suggestedOwnerId` came from (spec 1 §3's owner fallback chain) —
   * drives the D9 warning outline together with `ownerUnverified`. Owner no
   * longer blocks Send (the backend always resolves one), so an empty pick
   * is not itself a reason for the outline any more; only
   * `'integration_account'` is — the fallback ran out of configured
   * defaults and landed on the account that connects the CRM, so nobody
   * will be assigned to follow this lead up. Optional deploy-skew field
   * (an older backend's preview response won't carry it) — `undefined`
   * draws no outline, same as any other unresolved provenance.
   */
  ownerProvenance?: OwnerProvenance;
  /**
   * True only for a configured conference/company default owner id the
   * backend could not confirm against the CRM's live, assignable user
   * list — the other half of the D9 outline. Optional deploy-skew field,
   * same convention as `ownerProvenance`; `undefined`/`false` both draw no
   * outline.
   */
  ownerUnverified?: boolean;
  disabled: boolean; // !canManage
  onPick: (ownerId: string) => void;
  /**
   * Optional accessible name for the underlying `Select` (review fix, p3
   * Task 4 — additive-optional, the repo's own extension rule). OwnerPicker
   * has no row identity of its own (no `displayName` in its locked props),
   * so a caller with more than one row on screen — MatchView — composes and
   * passes this the same way it labels its checkboxes, so each row's owner
   * combobox has a distinct accessible name instead of sharing the
   * placeholder-derived default.
   */
  ariaLabel?: string;
}

/** `{name} — {role}`, shared with MatchView's bulk-assign toolbar Select. */
export function ownerLabel(owner: CrmOwner): string {
  return owner.role ? `${owner.name} (${owner.role})` : owner.name;
}

/**
 * Match view's owner column control (p3 Task 4). Decides its own display
 * mode from `ownerRequired`: when an owner isn't required, it's read-only —
 * `currentOwner`'s name as plain text, or nothing at all. When required, it's
 * an antd `Select` (`showSearch`) over `owners`; absent an explicit `value`,
 * it shows `suggestedOwnerId` (if any) with a small "Suggested" `Tag`
 * alongside — the badge disappears the moment the operator makes an explicit
 * pick.
 *
 * The warning outline (D9, repointed in p3 Task 3) no longer means "no owner
 * chosen" — the backend's fallback chain (spec 1 §3) always resolves one, so
 * owner never blocks Send and an empty pick alone is not a warning. It flags
 * the two resolutions still worth a second look before sending:
 * `ownerProvenance === 'integration_account'` (the fallback ran out of
 * configured defaults, so nobody will be assigned to follow this lead up) or
 * `ownerUnverified` (a configured default the backend could not confirm
 * against the CRM's user list). A cleanly resolved owner — a rep match, or a
 * verified conference/company default — gets no outline.
 */
export function OwnerPicker({
  owners,
  value,
  suggestedOwnerId,
  currentOwner,
  ownerRequired,
  ownerProvenance,
  ownerUnverified,
  disabled,
  onPick,
  ariaLabel,
}: OwnerPickerProps) {
  if (!ownerRequired) {
    return currentOwner ? <Typography.Text>{currentOwner.name}</Typography.Text> : null;
  }

  const selectValue = value ?? suggestedOwnerId ?? undefined;
  const showSuggestedBadge = !value && !!suggestedOwnerId;
  const isIntegrationAccount = ownerProvenance === 'integration_account';
  const isUnverified = ownerUnverified === true;
  const showWarning = isIntegrationAccount || isUnverified;
  // The two cases are mutually exclusive in practice (`ownerUnverified` only
  // ever describes a *configured* default, and `integration_account` means
  // no default was configured at all), but pick a definite one rather than
  // leaving the branch unresolved if that invariant ever slips.
  // An existing record that already has an (unusable) owner keeps it when
  // nothing resolves — a different mechanism from a New row landing on the
  // integration account — so the hint says so (2026-10-02 final review #2).
  const warningHelp = isIntegrationAccount
    ? (currentOwner
      ? CRM_COPY.help.matchOwnerIntegrationAccountExisting
      : CRM_COPY.help.matchOwnerIntegrationAccount)
    : CRM_COPY.help.matchOwnerUnverified;
  // Assigned-to gets its own badge text and hint — the lead's own "Assigned
  // to" column named this owner, which is worth calling out by name rather
  // than folding into the generic rep-match wording (spec §5.2, §8 step 3).
  const suggestedHelp =
    ownerProvenance === 'assigned_to' ? CRM_COPY.help.matchOwnerAssignedTo : CRM_COPY.help.matchOwnerSuggested;
  const suggestedBadgeText =
    ownerProvenance === 'assigned_to' ? CRM_COPY.suggestedFromAssignedToBadge : CRM_COPY.suggestedBadge;
  const options = owners.map((o) => ({ value: o.id, label: ownerLabel(o) }));

  return (
    <Space size={space.xs} wrap>
      {/* The dot's only affordance used to be an HTML `title`, which neither a
          keyboard nor a screen reader reaches — the reachable trigger beside
          it now carries the same short label plus the way out. */}
      <Badge dot={showWarning} status="warning">
        <Select
          showSearch
          status={showWarning ? 'warning' : undefined}
          aria-label={ariaLabel}
          value={selectValue}
          placeholder={CRM_COPY.ownerPickerPlaceholder}
          disabled={disabled}
          // Room to shrink: this now sits in Match's half-width column beside
          // the "Suggested" tag, where the old 220 floor overflowed.
          style={{ minWidth: 180, maxWidth: 320 }}
          options={options}
          filterOption={(input, option) =>
            (option?.label as string | undefined)?.toLowerCase().includes(input.toLowerCase()) ?? false
          }
          onChange={(ownerId: string) => onPick(ownerId)}
        />
      </Badge>
      {showWarning && <HelpHint {...warningHelp} testId="crm-help-owner-warning" />}
      {showSuggestedBadge && (
        <>
          <Tag color="blue">{suggestedBadgeText}</Tag>
          {/* The one per-row hint that earns its place: design spec §9 requires
              the suggestion's provenance be apparent, and the badge alone
              never says WHY this person was picked or that it can be changed.
              Both the badge text and this hint are chosen by `ownerProvenance`,
              so an 'assigned_to' suggestion reads differently from a rep-match
              one. */}
          <HelpHint {...suggestedHelp} testId="crm-help-owner-suggested" />
        </>
      )}
    </Space>
  );
}
